import { getGlobalSettingsAccountMailboxInfo } from "owa-account-source-list-store";
import { getAccountScopeUserSettings } from "owa-session-store";
import saveWin32Style from "./mutators/saveWin32Style";
import { logUsage } from "owa-analytics";
import { isHosted } from "owa-config/lib/isHosted";
import { WIN32_STYLE_ENABLED_SETTING_NAME, setWin32StylingEnabledGetter } from "owa-theme-common";
import isWin32StylingEnabled from "./selectors/isWin32StylingEnabled";

/**
 * Seeds the Win32 style enabled flag from the primed boot setting.
 *
 * Called once at boot. After this the local store is the single source of truth
 * for the session, so an in-session Save is never overwritten by the boot source.
 */
export default function initializeWin32StyleStore(): void {
    const mailboxInfo = getGlobalSettingsAccountMailboxInfo();
    const primeSettings = getAccountScopeUserSettings(mailboxInfo)?.PrimeSettings;
    const item = primeSettings?.Items?.find(setting => typeof setting.Id === "string" &&
        setting.Id.toLowerCase() === WIN32_STYLE_ENABLED_SETTING_NAME);
    const value = item?.Value?.[0]?.value;
    // Distinguish absent or malformed preferences from an explicit 'false' value.
    let preferenceState = "valid";
    if (!item) {
        preferenceState = "missingSetting";
    }
    else if (value === undefined) {
        preferenceState = "missingValue";
    }
    else if (value !== "true" && value !== "false") {
        preferenceState = "invalidValue";
    }
    const preferenceEnabled = value === "true";
    saveWin32Style(preferenceEnabled);
    setWin32StylingEnabledGetter(isWin32StylingEnabled);
    
    const hosted = isHosted();
    // Report the first blocker: host restriction, then preference state.
    let disableReason = "none";
    if (hosted) {
        disableReason = "hosted";
    }
    else if (preferenceState !== "valid") {
        disableReason = preferenceState;
    }
    else if (!preferenceEnabled) {
        disableReason = "userPreference";
    }
    
    logUsage("InitializeWin32Style", mailboxInfo, {
        preferenceState,
        preferenceEnabled,
        effectiveEnabled: !hosted && preferenceEnabled,
        disableReason,
        hosted,
        settingsSource: "bootSettings",
        // Boot settings do not include upstream read status; missing values do not prove a read failure.
        settingsReadOutcome: "unknown",
        primeSettingsPresent: primeSettings !== undefined && primeSettings !== null,
    });
}
